/**
 * Extensible situation features for judgment (roles/tags over defId===).
 * New jobs/monsters/scenarios add features here — not hard-coded branches in core.
 */
import type { RaidUnit } from "../raid-types";

export type SituationFeatureId =
  | "ally_critical_hp"
  | "owner_out_of_staff"
  | "pack_chew_feet"
  | "danger_zone_feet"
  | "healer_alive";

export type SituationFeatureBag = {
  id: SituationFeatureId;
  /** 0..1 strength when present. */
  strength: number;
  /** Optional subject unit id (e.g. critical ally). */
  subjectId?: number;
};

export function allyHpRatio(u: Pick<RaidUnit, "hp" | "maxHp">): number {
  if (!(u.maxHp > 0)) return 1;
  return Math.max(0, Math.min(1, u.hp / u.maxHp));
}

/** Soft-HP critical signal — ownership triage input (not packChew priority). */
export function criticalHp01(
  u: Pick<RaidUnit, "hp" | "maxHp">,
  belowPct: number,
): number {
  const r = allyHpRatio(u);
  // Fullheal plateau: belowPct≥1 → any missing HP is a triage signal.
  if (belowPct >= 1) {
    if (r >= 1) return 0;
    return Math.max(0, Math.min(1, 1 - r));
  }
  if (r >= belowPct) return 0;
  const span = Math.max(1e-6, belowPct);
  return Math.max(0, Math.min(1, (belowPct - r) / span));
}
